import { useState, useEffect, useCallback } from "react";
import { FaEdit, FaTrash, FaKey } from "react-icons/fa";
import ConfirmModal from "../employee/delete_modal";
import UserFormModal from "./adduser";
import ResetPasswordModal from "./resetpassword";
import { toast } from "react-toastify";
import { getUsers, deleteUser } from "../services/userservice";
import { getRoles } from "../services/rbacservice";
import api from "../services/api";
import Can from "../common/can";
import Pagination from "../common/pagination";
import { useAuth } from "../context/authcontext";

const PAGE_SIZE = 20;

const Users = () => {
  const { hasAnyPermission, hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const canShowActions = isAdmin || hasAnyPermission(["user.reset_password", "user.update", "user.delete"]);
  const [users, setUsers]         = useState([]);
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [page, setPage]           = useState(1);
  const [totalCount, setTotal]    = useState(0);

  const [search, setSearch]             = useState("");
  const [openModal, setOpenModal]       = useState(false);
  const [editUser, setEditUser]         = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Reset Password State — the modal itself now owns all the
  // password/notify/result state; this component only needs to know
  // *which* user (if any) is being reset, same as deleteTarget below.
  const [resetTarget, setResetTarget] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      const res = await getUsers(params);
      const data = res.data || res;
      const meta = res.meta || {};
      const rawUsers = Array.isArray(data) ? data : data.results || [];
      setUsers(rawUsers.filter(u => !u.is_superuser));
      setTotal(meta.count ?? data.count ?? (Array.isArray(data) ? data.length : 0));
    } catch { toast.error("Failed to load users"); }
    finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => {
    fetchUsers();
    api.get("dropdowns/employees/").then((r) => setEmployees(r.data.data || r.data || [])).catch(() => {});
    getRoles().then((r) => setRoles(r || [])).catch(() => {});
  }, [fetchUsers]);

  useEffect(() => { setPage(1); }, [search]);

  const openAdd = () => {
    setEditUser(null);
    setOpenModal(true);
  };
  const openEdit = (u) => {
    setEditUser(u);
    setOpenModal(true);
  };

  const confirmDelete = async (password) => {
    try {
      await deleteUser(deleteTarget.id, password);
      toast.success("User deleted successfully");
      fetchUsers();
      setDeleteTarget(null);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.detail || err.response?.data?.error || "Incorrect password or permission denied";
      toast.error(msg);
      throw new Error(msg);
    }
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <div className="container">
      <div className="page-header">
        <h2>User Management</h2>
        <Can perm="user.create">
          <button className="primary-btn" onClick={openAdd}>Add User</button>
        </Can>
      </div>

      <div className="filters-card single">
        <input className="search-input" placeholder="🔍 Search users..." value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {loading ? (
        <div className="empty-state"><p>Loading users…</p></div>
      ) : (
        <table className="table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Employee</th><th>Assigned Role(s)</th><th>Status</th>
            {/* <th className="col-last-login">Last Login</th> */}
            {canShowActions && <th>Actions</th>}</tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr><td colSpan={canShowActions ? "7" : "6"}><div className="empty-state"><p>No Users Found</p></div></td></tr>
            ) : (
              users.map((u) => (
                <tr key={u.id}>
                  <td><strong>{u.full_name || `${u.first_name} ${u.last_name}`}</strong></td>
                  <td>{u.email}</td>
                  <td>{u.employee_detail?.full_name || u.employee_name || "—"}</td>
                  <td>
                    <div className="role-badges-cell">
                      {u.roles && u.roles.length > 0 ? (
                        u.roles.map((r, idx) => (
                          <span key={typeof r === "object" ? (r.id || r.name || idx) : r} className="role-badge-pill">
                            {typeof r === "object" ? r.name : r}
                          </span>
                        ))
                      ) : u.role ? (
                        <span className="role-badge-pill">
                          {typeof u.role === "object" ? u.role.name : u.role}
                        </span>
                      ) : (
                        <span className="role-badge-empty">No Role Assigned</span>
                      )}
                    </div>
                  </td>
                  <td><span className={`emp-status ${u.is_active ? "active" : "inactive"}`}>{u.is_active ? "Active" : "Inactive"}</span></td>
                  {/* <td className="col-last-login">{u.last_login ? new Date(u.last_login).toLocaleDateString() : "Never"}</td> */}
                  {canShowActions && (
                    <td className="user-actions-cell">
                      <div className="user-action-icons">
                        <Can perm="user.reset_password">
                          <button
                            className="icon-btn edit"
                            title="Reset User Password"
                            onClick={() => setResetTarget(u)}
                          >
                            <FaKey />
                          </button>
                        </Can>
                        <Can perm="user.update">
                          <button className="icon-btn edit" title="Edit User" onClick={() => openEdit(u)}><FaEdit /></button>
                        </Can>
                        <Can perm="user.delete">
                          <button className="icon-btn delete" title="Delete User" onClick={() => setDeleteTarget(u)}><FaTrash /></button>
                        </Can>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      <Pagination page={page} totalPages={totalPages} setPage={setPage} />

      {openModal && (
        <UserFormModal
          editUser={editUser}
          employees={employees}
          roles={roles}
          onClose={() => setOpenModal(false)}
          onSaved={() => {
            fetchUsers();
            setOpenModal(false);
          }}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete User"
          message={`Delete user "${deleteTarget.email}"?`}
          confirmText="Delete"
          type="danger"
          requirePassword={true}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {resetTarget && (
        <ResetPasswordModal
          target={resetTarget}
          onClose={() => setResetTarget(null)}
        />
      )}
    </div>
  );
};

export default Users;